import axios from 'axios'
import Cookies from 'js-cookie'
import { useToast } from 'vue-toastification'

const toast = useToast()

// 创建API实例
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  timeout: 300000, // 5分钟超时（支持AI生成等耗时操作）
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
})

// 请求拦截器 - 添加认证token和CSRF token
api.interceptors.request.use(
  (config) => {
    // 添加Authorization token
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Token ${token}`
    }

    // 添加CSRF token (Django需要)
    const csrfToken = Cookies.get('csrftoken')
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken
    }

    return config
  },
  (error) => {
    console.error('请求配置错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器 - 处理认证错误和通用错误
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const { response } = error
    
    if (!response) {
      toast.error('网络连接错误，请检查网络设置')
      return Promise.reject(error)
    }

    const { status, data } = response

    switch (status) {
      case 401:
        // 如果是主动退出登录，不显示错误提示
        if (!window.isLoggingOut) {
          // 未认证 - 清除本地token并跳转登录
          localStorage.removeItem('auth_token')
          localStorage.removeItem('user_info')
          toast.error('登录已过期，请重新登录')
          // 这里可以使用router跳转到登录页
          window.location.href = '/login'
        }
        break
      
      case 403:
        // 如果是主动退出登录，不显示错误提示
        if (!window.isLoggingOut) {
          toast.error('没有权限执行此操作')
        }
        break
      
      case 404:
        toast.error('请求的资源不存在')
        break
      
      case 422:
        // Django REST framework 验证错误
        if (data?.detail) {
          toast.error(data.detail)
        } else if (data?.errors) {
          // 处理字段验证错误
          const errorMessages = []
          Object.keys(data.errors).forEach(field => {
            const fieldErrors = data.errors[field]
            if (Array.isArray(fieldErrors)) {
              errorMessages.push(...fieldErrors)
            } else {
              errorMessages.push(fieldErrors)
            }
          })
          toast.error(errorMessages.join(', '))
        }
        break
      
      case 500:
        toast.error('服务器内部错误，请稍后重试')
        break
      
      default:
        // 对于400状态码（如登录失败），不在这里显示错误提示
        // 让具体的组件自己处理错误显示
        if (status !== 400) {
          toast.error(data?.detail || data?.message || '操作失败')
        }
    }

    return Promise.reject(error)
  }
)

export default api

// API 请求封装函数
export const apiRequest = {
  // GET 请求
  get: (url, params = {}, config = {}) => {
    return api.get(url, { params, ...config })
  },

  // POST 请求
  post: (url, data = {}, config = {}) => {
    return api.post(url, data, config)
  },

  // PUT 请求
  put: (url, data = {}, config = {}) => {
    return api.put(url, data, config)
  },

  // PATCH 请求
  patch: (url, data = {}, config = {}) => {
    return api.patch(url, data, config)
  },

  // DELETE 请求
  delete: (url, config = {}) => {
    return api.delete(url, config)
  },

  // 文件上传
  upload: (url, formData, onUploadProgress = null) => {
    return api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    })
  }
}
